import { Input, Modal, Select } from 'antd';
import type { EdgeSingular } from 'cytoscape';
import React, { useEffect, useMemo } from 'react';
import {
  EdgeType,
  hierarchicalStructuralEdges,
  LinkQualifier,
  LinkNegation,
} from '../model/edge-model';
import { Affiliation, Essence, MMNode, NodeType } from '../model/node-model';
import agentImg from './../data/edge-type-images/agent.svg';
import aggregationImg from './../data/edge-type-images/aggregation.svg';
import classificationImg from './../data/edge-type-images/classification.svg';
import consumptionResultImg from './../data/edge-type-images/consumption-result.svg';
import effectImg from './../data/edge-type-images/effect.svg';
import exhibitionImg from './../data/edge-type-images/exhibition.svg';
import generalizationImg from './../data/edge-type-images/generalization.svg';
import generationImg from './../data/edge-type-images/generation.svg';
import influenceImg from './../data/edge-type-images/influence.svg';
import invocationImg from './../data/edge-type-images/invocation.svg';
import instrumentImg from './../data/edge-type-images/instrument.svg';
import taggedImg from './../data/edge-type-images/tagged.svg';
import {
  buildMarkerLabel,
  getAllowedNegations,
  getEdgeMarkerOption,
} from '../options/link-marker-config';
import {
  buildOplContext,
  generateOplSentence,
  getAllowedEdgeTypesByRecords,
  OplEdgeRecord,
  OplNodeRecord,
} from '../utils/opl-rules';

// 辅助：按节点类型返回高亮颜色
export const getEdgeColor = (type?: string) => {
  if (type === 'object') return '#70E483';
  if (type === 'process') return '#3BC3FF';
  if (type === 'state') return 'orange';
  if (type === 'value') return '#4169E1';
  return '#132347';
};

const toNodeRecordFromMM = (node: MMNode): OplNodeRecord => {
  const parentId =
    node.parent && (node.parent as MMNode).id ? (node.parent as MMNode).id : '';
  return {
    id: node.id,
    label: node.label,
    type: node.type,
    parentId,
    childGroupType: node.childGroupType,
    essence: node.essence,
    affiliation: node.affiliation,
  };
};

interface EdgeEditDialogProps {
  open: boolean;
  mode?: 'create' | 'edit';
  edge: EdgeSingular | null;
  edgeType: EdgeType | null;
  edgeLabel: string;
  linkQualifier: LinkQualifier;
  linkNegation: LinkNegation;
  directionInfo?: {
    sourceId?: string;
    targetId?: string;
    sourceLabel: string;
    targetLabel: string;
    sourceType?: string;
    targetType?: string;
    sourceMMRef?: MMNode | null;
    targetMMRef?: MMNode | null;
  } | null;
  onEdgeTypeChange: (nextType: EdgeType) => void;
  onEdgeLabelChange: (nextLabel: string) => void;
  onLinkQualifierChange: (next: LinkQualifier) => void;
  onLinkNegationChange: (next: LinkNegation) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

// 结构关系：现有六类连线全部归入该组
const STRUCTURAL_EDGE_OPTIONS: Array<{
  type: EdgeType;
  name: string;
  img: string;
}> = [
  { type: EdgeType.Aggregation, name: '聚合关系', img: aggregationImg },
  { type: EdgeType.Exhibition, name: '展示关系', img: exhibitionImg },
  { type: EdgeType.Generalization, name: '泛化关系', img: generalizationImg },
  { type: EdgeType.Classification, name: '实例化关系', img: classificationImg },
  { type: EdgeType.Tagged, name: '单向关联关系', img: taggedImg },
  { type: EdgeType.Bidirectional, name: '对等关联关系', img: effectImg },
];

// 过程关系：先按图形全量开放，后续再细分规则
const PROCESS_EDGE_OPTIONS: Array<{
  type: EdgeType;
  name: string;
  img: string;
}> = [
  { type: EdgeType.Agent, name: 'Agent使能', img: agentImg },
  { type: EdgeType.Instrument, name: '工具', img: instrumentImg },
  { type: EdgeType.Consumption, name: '消耗', img: consumptionResultImg },
  { type: EdgeType.Generation, name: '生成', img: generationImg },
  { type: EdgeType.Effect, name: '影响', img: influenceImg },
  { type: EdgeType.Invocation, name: '调用', img: invocationImg },
];

const EdgeEditDialog: React.FC<EdgeEditDialogProps> = ({
  open,
  mode = 'edit',
  edge,
  edgeType,
  edgeLabel,
  linkQualifier,
  linkNegation,
  directionInfo,
  onEdgeTypeChange,
  onEdgeLabelChange,
  onLinkQualifierChange,
  onLinkNegationChange,
  onConfirm,
  onCancel,
}) => {
  // 方向描述：高亮源/目标节点名称
  const mergedDirectionInfo = useMemo(() => {
    if (directionInfo) {
      return {
        sourceLabel: directionInfo.sourceLabel,
        targetLabel: directionInfo.targetLabel,
        sourceColor: getEdgeColor(directionInfo.sourceType),
        targetColor: getEdgeColor(directionInfo.targetType),
      };
    }

    if (!edge) return null;
    const src = edge.source();
    const tgt = edge.target();
    const sourceLabel = src?.data?.('label') ?? '';
    const targetLabel = tgt?.data?.('label') ?? '';
    return {
      sourceLabel,
      targetLabel,
      sourceColor: getEdgeColor(src?.data?.('type')),
      targetColor: getEdgeColor(tgt?.data?.('type')),
    };
  }, [directionInfo, edge]);

  const endpointRecords = useMemo(() => {
    const recordMap = new Map<string, OplNodeRecord>();
    const pushMM = (mm: MMNode | null | undefined) => {
      if (!mm || recordMap.has(mm.id)) return;
      recordMap.set(mm.id, toNodeRecordFromMM(mm));
      if (mm.type === NodeType.Object) {
        (mm.children || []).forEach(child => {
          if (child.type === NodeType.State && !child.deleted) {
            pushMM(child as MMNode);
          }
        });
      }
      if (mm.type === NodeType.State) {
        const parentMM = mm.parent as MMNode;
        if (parentMM && (parentMM as MMNode).id) {
          pushMM(parentMM);
        }
      }
    };

    const pushCyNode = (node: any) => {
      if (!node || !node.id) return;
      const id = typeof node.id === 'function' ? node.id() : node.id;
      if (!id || recordMap.has(id)) return;
      const data = node.data ? node.data() : {};
      const parentId =
        data.parent || (node.isChild?.() ? node.parent()?.id?.() : '');
      recordMap.set(id, {
        id,
        label: data.label || data.displayLabel || id,
        type: data.type,
        parentId,
        childGroupType: data.childGroupType,
        essence: data.essence as Essence,
        affiliation: data.affiliation as Affiliation,
      });

      if (data.type === NodeType.Object && node.children) {
        const stateChildren = node.children('[type = "state"]');
        stateChildren?.forEach((child: any) => pushCyNode(child));
      }
      if (data.type === NodeType.State && parentId) {
        const parentNode = node.parent?.();
        if (parentNode && parentNode.length) {
          pushCyNode(parentNode);
        }
      }
    };

    const edgeSource = edge ? edge.source() : null;
    const edgeTarget = edge ? edge.target() : null;
    const sourceMM =
      directionInfo?.sourceMMRef ||
      (edgeSource ? (edgeSource.data('MMRef') as MMNode) : null);
    const targetMM =
      directionInfo?.targetMMRef ||
      (edgeTarget ? (edgeTarget.data('MMRef') as MMNode) : null);

    if (sourceMM) pushMM(sourceMM);
    if (targetMM) pushMM(targetMM);
    if (!sourceMM && edgeSource) pushCyNode(edgeSource);
    if (!targetMM && edgeTarget) pushCyNode(edgeTarget);
    if (!recordMap.size && directionInfo) {
      if (directionInfo.sourceId || directionInfo.sourceLabel) {
        const sid = directionInfo.sourceId || directionInfo.sourceLabel;
        recordMap.set(sid, {
          id: sid as string,
          label: directionInfo.sourceLabel,
          type: directionInfo.sourceType || '',
        });
      }
      if (directionInfo.targetId || directionInfo.targetLabel) {
        const tid = directionInfo.targetId || directionInfo.targetLabel;
        recordMap.set(tid, {
          id: tid as string,
          label: directionInfo.targetLabel,
          type: directionInfo.targetType || '',
        });
      }
    }

    return {
      nodes: Array.from(recordMap.values()),
      sourceId:
        (sourceMM && sourceMM.id) ||
        directionInfo?.sourceId ||
        (edgeSource ? edgeSource.id() : null),
      targetId:
        (targetMM && targetMM.id) ||
        directionInfo?.targetId ||
        (edgeTarget ? edgeTarget.id() : null),
    };
  }, [edge, directionInfo]);

  const oplContext = useMemo(() => {
    if (!endpointRecords.nodes.length) return null;
    return buildOplContext(endpointRecords.nodes);
  }, [endpointRecords]);

  const allowedEdgeTypes = useMemo(() => {
    if (!oplContext || !endpointRecords.sourceId || !endpointRecords.targetId)
      return [];
    return getAllowedEdgeTypesByRecords(
      endpointRecords.sourceId,
      endpointRecords.targetId,
      oplContext
    );
  }, [oplContext, endpointRecords]);

  const allowedEdgeTypeSet = useMemo(
    () => new Set(allowedEdgeTypes),
    [allowedEdgeTypes]
  );

  const structuralEdgeOptions = useMemo(
    () =>
      STRUCTURAL_EDGE_OPTIONS.filter(
        item => allowedEdgeTypeSet.has(item.type) || edgeType === item.type
      ),
    [allowedEdgeTypeSet, edgeType]
  );

  const processEdgeOptions = useMemo(
    () =>
      PROCESS_EDGE_OPTIONS.filter(
        item => allowedEdgeTypeSet.has(item.type) || edgeType === item.type
      ),
    [allowedEdgeTypeSet, edgeType]
  );

  const oplPreviewText = useMemo(() => {
    if (
      !oplContext ||
      !endpointRecords.sourceId ||
      !endpointRecords.targetId ||
      !edgeType
    )
      return null;
    const edgeRecord: OplEdgeRecord = {
      id: 'preview',
      sourceId: endpointRecords.sourceId,
      targetId: endpointRecords.targetId,
      type: edgeType,
      label: edgeLabel,
      linkQualifier,
      linkNegation,
    };
    return generateOplSentence(edgeRecord, oplContext);
  }, [
    oplContext,
    endpointRecords,
    edgeType,
    edgeLabel,
    linkQualifier,
    linkNegation,
  ]);

  const oplPreviewContent = useMemo(() => {
    if (!oplPreviewText || !oplContext) return oplPreviewText;
    const nodeInfos = endpointRecords.nodes.map(node => ({
      id: node.id,
      label: node.label,
      color: getEdgeColor(node.type as string),
    }));
    if (!nodeInfos.length) return oplPreviewText;

    const escapeReg = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const text = oplPreviewText;
    const hits: { start: number; end: number; color: string }[] = [];
    nodeInfos.forEach(info => {
      const pattern = escapeReg(info.label).replace(/\\\s+/g, '\\s+');
      const regex = new RegExp(pattern, 'gi');
      let match: RegExpExecArray | null;
      while ((match = regex.exec(text)) !== null) {
        hits.push({
          start: match.index,
          end: match.index + match[0].length,
          color: info.color,
        });
        if (match.index === regex.lastIndex) regex.lastIndex++;
      }
    });
    if (!hits.length) return text;

    hits.sort((a, b) =>
      a.start === b.start
        ? b.end - b.start - (a.end - a.start)
        : a.start - b.start
    );
    const merged: typeof hits = [];
    hits.forEach(hit => {
      const last = merged[merged.length - 1];
      if (!last || hit.start >= last.end) merged.push(hit);
    });

    const parts: React.ReactNode[] = [];
    let cursor = 0;
    merged.forEach(hit => {
      if (cursor < hit.start) parts.push(text.slice(cursor, hit.start));
      parts.push(
        <span
          key={`${hit.start}-${hit.end}`}
          style={{ color: hit.color, fontWeight: 600 }}
        >
          {text.slice(hit.start, hit.end)}
        </span>
      );
      cursor = hit.end;
    });
    if (cursor < text.length) parts.push(text.slice(cursor));
    return parts;
  }, [oplPreviewText, oplContext, endpointRecords.nodes]);

  const selectionAllowed = useMemo(
    () => (edgeType ? allowedEdgeTypeSet.has(edgeType) : false),
    [edgeType, allowedEdgeTypeSet]
  );

  const noStructuralAvailable = structuralEdgeOptions.length === 0;
  const noProcessAvailable = processEdgeOptions.length === 0;
  const okDisabled = !edgeType || !selectionAllowed;

  const canEditLabel = useMemo(
    () => (edgeType ? !hierarchicalStructuralEdges.includes(edgeType) : false),
    [edgeType]
  );
  const isStructuralGroup = edgeType
    ? STRUCTURAL_EDGE_OPTIONS.some(option => option.type === edgeType)
    : false;
  const markerOption = useMemo(() => getEdgeMarkerOption(edgeType), [edgeType]);
  const allowedNegations = useMemo(() => {
    if (!markerOption) return [];
    return getAllowedNegations(markerOption, linkQualifier);
  }, [markerOption, linkQualifier]);

  useEffect(() => {
    if (!markerOption) return;
    if (allowedNegations.length && !allowedNegations.includes(linkNegation)) {
      onLinkNegationChange(allowedNegations[0]);
    }
  }, [markerOption, allowedNegations, linkNegation, onLinkNegationChange]);

  return (
    <Modal
      title={mode === 'create' ? '创建连线' : '编辑连线'}
      open={open}
      onOk={onConfirm}
      onCancel={onCancel}
      okText={mode === 'create' ? '创建' : '确认'}
      cancelText="取消"
      okButtonProps={{ disabled: okDisabled }}
      width={680}
      zIndex={1300}
    >
      <div className="space-y-4">
        {/* {mode === 'edit' && (
          <div className="text-xs text-gray-500">
            {edge ? `EdgeId：${edge.id()}` : ''}
          </div>
        )} */}

        {mergedDirectionInfo && (
          <div className="text-sm text-gray-700">
            连线方向：
            <span style={{ color: mergedDirectionInfo.sourceColor }}>
              {mergedDirectionInfo.sourceLabel || '（未命名）'}
            </span>
            {' → '}
            <span style={{ color: mergedDirectionInfo.targetColor }}>
              {mergedDirectionInfo.targetLabel || '（未命名）'}
            </span>
          </div>
        )}
        {!selectionAllowed && edgeType && (
          <div className="text-xs text-red-500">
            当前节点组合不支持所选连线类型，请重新选择。
          </div>
        )}

        {structuralEdgeOptions.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              结构关系：
            </label>
            <div className="grid grid-cols-3 gap-3">
              {structuralEdgeOptions.map(item => (
                <div
                  key={item.type}
                  className={`border-2 rounded-lg p-3 cursor-pointer transition-all text-center ${
                    edgeType === item.type
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => onEdgeTypeChange(item.type)}
                >
                  <div className="mb-2 flex justify-center relative">
                    <img
                      src={item.img}
                      alt={item.name}
                      className="h-10 w-auto select-none"
                      draggable={false}
                    />
                    {getEdgeMarkerOption(item.type) &&
                      edgeType === item.type && (
                        <span className="absolute left-1/2 -translate-x-1/2 -top-3 text-2xl font-bold text-[#586D8C] pointer-events-none">
                          {buildMarkerLabel(linkQualifier, linkNegation)}
                        </span>
                      )}
                  </div>
                  <div
                    className={`text-xs font-medium ${
                      edgeType === item.type ? 'text-blue-600' : 'text-gray-600'
                    }`}
                  >
                    {item.name}
                  </div>
                </div>
              ))}
            </div>
            {isStructuralGroup && canEditLabel && (
              <div className="mt-4 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-3">
                <label className="block text-xs font-medium text-gray-500 mb-2">
                  Tag（结构关系）：
                </label>
                <Input
                  value={edgeLabel}
                  onChange={e => onEdgeLabelChange(e.target.value)}
                  placeholder={
                    canEditLabel
                      ? '请输入 Tag（可选）'
                      : '结构关系不支持填写 Label'
                  }
                  disabled={!canEditLabel}
                />
              </div>
            )}
          </div>
        )}

        {processEdgeOptions.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              过程关系：
            </label>
            <div className="grid grid-cols-3 gap-3">
              {processEdgeOptions.map(item => (
                <div
                  key={item.type}
                  className={`border-2 rounded-lg p-3 cursor-pointer transition-all text-center ${
                    edgeType === item.type
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => onEdgeTypeChange(item.type)}
                >
                  <div className="mb-2 flex justify-center relative">
                    <img
                      src={item.img}
                      alt={item.name}
                      className="h-10 w-auto select-none"
                      draggable={false}
                    />
                    {getEdgeMarkerOption(item.type) &&
                      edgeType === item.type && (
                        <span className="absolute left-1/2 -translate-x-1/2 -top-3 text-xl font-bold text-[#586D8C] pointer-events-none">
                          {buildMarkerLabel(linkQualifier, linkNegation)}
                        </span>
                      )}
                  </div>
                  <div
                    className={`text-xs font-medium ${
                      edgeType === item.type ? 'text-blue-600' : 'text-gray-600'
                    }`}
                  >
                    {item.name}
                  </div>
                </div>
              ))}
            </div>
            {markerOption && edgeType && (
              <div className="mt-4 space-y-3">
                <div className="text-xs text-gray-500">关系修饰与否定：</div>
                <div
                  className={
                    allowedNegations.length > 1
                      ? 'grid grid-cols-2 gap-3'
                      : 'grid grid-cols-1 gap-3'
                  }
                >
                  {allowedNegations.length > 1 && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        否定（Negation）
                      </label>
                      <Select
                        size="small"
                        value={
                          allowedNegations.includes(linkNegation)
                            ? linkNegation
                            : allowedNegations[0]
                        }
                        onChange={val => onLinkNegationChange(val)}
                        className="w-full"
                        options={allowedNegations.map(n => ({
                          value: n,
                          label: n === LinkNegation.None ? 'None' : 'Not',
                        }))}
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      修饰（Qualifier）
                    </label>
                    <Select
                      size="small"
                      value={linkQualifier}
                      onChange={val => onLinkQualifierChange(val)}
                      className="w-full"
                      options={markerOption.qualifierOptions.map(q => ({
                        value: q,
                        label:
                          q === LinkQualifier.None
                            ? `${edgeType}`
                            : `${edgeType} ${
                                q === LinkQualifier.Condition
                                  ? 'Condition'
                                  : 'Event'
                              }`,
                      }))}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {oplPreviewText && (
        <div className="mt-4 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-3 text-sm">
          <div className="text-xs text-gray-500 mb-1">
            OPL ：
            {!selectionAllowed && edgeType
              ? '（当前类型不在支持范围，展示默认预览）'
              : ''}
          </div>
          <div className="font-medium leading-6">{oplPreviewContent}</div>
        </div>
      )}
    </Modal>
  );
};

export default EdgeEditDialog;
